<template>
  <v-form ref="form" v-model="isFormValid" @submit.prevent="onSubmit">
    <v-card class="fill-height info_card" title="Cast and Crew">
      <template v-slot:append>
        <v-tooltip text="Add Cast/Crew">
          <template v-slot:activator="{ props: activatorProps }">
            <v-icon color="primary" v-bind="activatorProps" @click="addCastCrew"
              >mdi-account-plus-outline</v-icon
            >
          </template>
        </v-tooltip>
      </template>
      <v-card-text>
        <div v-html="year.turn_in_cast_text"></div>
        <v-container>
          <draggable
            v-model="castCrews"
            item-key="order"
            :group="{ name: 'fields', pull: 'clone', put: false }"
            :animation="200"
            :ghostClass="'ghost'"
            :forceFallback="true"
            handle=".cursor-move"
            @end="onDragEnd">
            <template #item="{ element }">
              <CastCrewComponent
                :cast-crew="element"
                :is-last="isLast"
                @remove="removeCast"
                @updated="updateCast">
              </CastCrewComponent>
            </template>
          </draggable>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <FullWidthButton :disabled="!isFormValid" @click="onSubmit">Save</FullWidthButton>
      </v-card-actions>
    </v-card>
  </v-form>
</template>
<script setup lang="ts">
import type { SortableEvent } from 'sortablejs';
import draggable from 'vuedraggable';
import CastCrewComponent from '@/components/castandcrew/CastCrewComponent.vue';
import {
  ITeamCriteriaDTO,
  IYearDTO,
  ITeamDTO,
  TeamCriteriaDTO,
  ISubmissionCastCrewDTO,
  SubmissionCastCrewDTO,
} from '@/dtos';
import { useCastCrewStore } from '@/stores/modules/castcrew';
import { computed, ref, watch } from 'vue';
import type { VForm } from 'vuetify/components';
import { useReleaseStore } from '@/stores';

interface Props {
  registration: ITeamCriteriaDTO;
  year: IYearDTO;
  team: ITeamDTO;
}

const props = defineProps<Props>();
const year = computed(() => props.year);
const registration = computed(() => props.registration);
const submission = computed(() => registration.value.submissions[0] ?? new TeamCriteriaDTO());
const submissionId = computed(() => submission.value.id);
const isFormValid = ref<boolean>(false);
const form = ref<InstanceType<typeof VForm> | null>();
const castCrewStore = useCastCrewStore();
const releaseStore = useReleaseStore();
const { data } = castCrewStore.getCastAndCrew(submissionId);

const castCrews = ref<ISubmissionCastCrewDTO[]>([]);
const isLast = computed(() => castCrews.value.length <= 1);

watch(
  () => data.value,
  (newData) => {
    if (newData) {
      castCrews.value = newData.data.map((crew) => new SubmissionCastCrewDTO(crew));
    }
    if (castCrews.value.length === 0) {
      castCrews.value = [new SubmissionCastCrewDTO()];
    }
  },
  { immediate: true },
);

const onSubmit = async () => {
  if (form.value && (await form.value.validate())) {
    await castCrewStore.updateCastAndCrew.mutateAsync({
      submissionId: submissionId.value,
      castAndCrews: castCrews.value,
    });
    let completed = true;
    for (const cast of castCrews.value) {
      if (cast.castCrew?.name === 'Actor/Actress' && !cast.release) {
        completed = false;
        break;
      }
    }
    if (completed) {
      await releaseStore.markDone.mutateAsync(submissionId.value);
    } else {
      await releaseStore.markNotDone.mutateAsync(submissionId.value);
    }
  } else {
    console.error('Form is invalid');
  }
};

const removeCast = (order: number) => {
  const index = castCrews.value.findIndex((crew) => crew.order === order);
  if (index !== -1) {
    castCrews.value.splice(index, 1);
  }
  if (castCrews.value.length === 0) {
    castCrews.value.push(new SubmissionCastCrewDTO());
  }
  sortCast();
};

const updateCast = (castCrew: ISubmissionCastCrewDTO, order: number) => {
  const index = castCrews.value.findIndex((crew) => crew.order === order);
  if (index !== -1) {
    castCrews.value[index] = new SubmissionCastCrewDTO(castCrew);
  } else {
    castCrews.value.push(new SubmissionCastCrewDTO(castCrew));
  }
  sortCast();
};

const sortCast = () => {
  castCrews.value.forEach((field, index) => {
    field.order = index + 1;
  });
};

function onDragEnd(evt: SortableEvent) {
  if (evt.oldIndex === evt.newIndex) {
    return;
  }
  sortCast();
}

const addCastCrew = () => {
  let cast = new SubmissionCastCrewDTO();
  cast.order = castCrews.value.length + 1;
  castCrews.value.push(cast);
  sortCast();
};
</script>
